import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDir, setSortDir] = useState('DESC');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [deleteCustomer, setDeleteCustomer] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        search,
        page: String(page),
        limit: '50',
        sortBy,
        sortDir,
        includeInactive: String(showInactive)
      });
      const res = await fetch(`/api/customers?${params}`);
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      if (data.status === 'success') {
        setCustomers(data.data.customers || []);
        setPagination(data.data.pagination || { total: 0, pages: 0 });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [search, page, sortBy, sortDir, showInactive]);

  useEffect(() => {
    const timer = setTimeout(() => fetchCustomers(), 250);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  // Auto-detect mobile to default to cards view
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setViewMode('cards');
    }
  }, []);

  const handleDelete = async (customer, mode = 'hard') => {
    try {
      const url = mode === 'soft'
        ? `/api/customers/${customer.id}?soft=true`
        : `/api/customers/${customer.id}`;
      const res = await fetch(url, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        setDeleteCustomer(null);
        fetchCustomers();
      } else {
        alert(data.message || 'Failed to delete customer');
      }
    } catch (err) {
      alert('Failed to delete customer: ' + err.message);
    }
  };

  const handleReactivate = async (customer) => {
    try {
      const res = await fetch(`/api/customers/${customer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: true })
      });
      if (res.ok) fetchCustomers();
    } catch (err) {
      alert('Failed to reactivate');
    }
  };

  const handleSaveEdit = async (customer) => {
    try {
      const res = await fetch(`/api/customers/${customer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customer)
      });
      if (res.ok) {
        setEditingCustomer(null);
        fetchCustomers();
      }
    } catch (err) {
      alert('Failed to update customer');
    }
  };

  const handleAddCustomer = async (customer) => {
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...customer, source: 'admin' })
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchCustomers();
      }
    } catch (err) {
      alert('Failed to add customer');
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="container mx-auto px-3 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <span>👥</span>
                <span>Customers</span>
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {pagination.total} total customers
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/pinvoices"
                className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition flex items-center gap-2 text-sm font-semibold"
              >
                <span>📄</span> New Invoice
              </Link>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center gap-2 text-sm font-semibold"
              >
                <span>➕</span> Add Customer
              </button>
            </div>
          </div>

          {/* Search & Controls */}
          <div className="mt-4 flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
              <input
                type="search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search by name, email, phone, address..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-3 border border-gray-200 rounded-xl bg-white text-sm"
              >
                <option value="updatedAt">Last Updated</option>
                <option value="createdAt">Date Added</option>
                <option value="name">Name</option>
                <option value="totalSpent">Total Spent</option>
                <option value="totalInvoices">Total Invoices</option>
                <option value="lastInvoiceDate">Last Invoice</option>
              </select>

              <button
                onClick={() => setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC')}
                className="px-3 py-3 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 text-sm"
                title={sortDir === 'ASC' ? 'Ascending' : 'Descending'}
              >
                {sortDir === 'ASC' ? '↑' : '↓'}
              </button>

              <div className="hidden md:flex border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-3 text-sm ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-3 text-sm ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  Table
                </button>
              </div>
            </div>
          </div>

          {/* Show Inactive Toggle */}
          <div className="mt-3 flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
                className="w-4 h-4 rounded text-blue-600"
              />
              <span>Show deactivated customers</span>
            </label>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <StatCard
            icon="👥"
            label="Total Customers"
            value={pagination.total}
            color="from-blue-500 to-blue-600"
          />
          <StatCard
            icon="💰"
            label="Total Revenue"
            value={formatCurrency(customers.reduce((sum, c) => sum + parseFloat(c.totalSpent || 0), 0))}
            color="from-green-500 to-green-600"
          />
          <StatCard
            icon="📄"
            label="Total Invoices"
            value={customers.reduce((sum, c) => sum + (c.totalInvoices || 0), 0)}
            color="from-purple-500 to-purple-600"
          />
          <StatCard
            icon="📋"
            label="Total Estimates"
            value={customers.reduce((sum, c) => sum + (c.totalEstimates || 0), 0)}
            color="from-orange-500 to-orange-600"
          />
        </div>

        {/* Loading / Error / Empty */}
        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-500">Loading customers...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">
            {error}
          </div>
        ) : customers.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-gray-700">No customers yet</h3>
            <p className="text-sm text-gray-500 mt-2">
              Customers will appear here when invoices are created on /pinvoices
            </p>
          </div>
        ) : viewMode === 'cards' ? (
          // CARDS VIEW (Mobile-friendly)
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {customers.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                onEdit={() => setEditingCustomer(customer)}
                onDelete={() => setDeleteCustomer(customer)}
                onReactivate={() => handleReactivate(customer)}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
              />
            ))}
          </div>
        ) : (
          // TABLE VIEW (Desktop)
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Address</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Invoices</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Spent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Invoice</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{customer.name}</div>
                        {customer.acModel && (
                          <div className="text-xs text-gray-500">Model: {customer.acModel}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {customer.email && (
                          <a href={`mailto:${customer.email}`} className="block text-blue-600 hover:underline">
                            ✉️ {customer.email}
                          </a>
                        )}
                        {customer.phone && (
                          <a href={`tel:${customer.phone}`} className="block text-gray-600">
                            📞 {customer.phone}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {customer.address || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-sm font-semibold">{customer.totalInvoices || 0}</div>
                        <div className="text-xs text-gray-500">{customer.totalEstimates || 0} estimates</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        {formatCurrency(customer.totalSpent)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(customer.lastInvoiceDate)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setEditingCustomer(customer)}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100 mr-1"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => setDeleteCustomer(customer)}
                          className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs hover:bg-red-100"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-4 flex items-center justify-between bg-white rounded-2xl shadow-sm p-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-100 rounded-xl disabled:opacity-50 text-sm font-medium"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {pagination.pages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="px-4 py-2 bg-gray-100 rounded-xl disabled:opacity-50 text-sm font-medium"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <CustomerFormModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={handleSaveEdit}
          title="Edit Customer"
        />
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <CustomerFormModal
          customer={{ name: '', email: '', phone: '', address: '', acModel: '', notes: '' }}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCustomer}
          title="Add New Customer"
        />
      )}

      {/* Delete Confirmation Modal - Choose Soft or Hard Delete */}
      {deleteCustomer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteCustomer(null); }}
        >
          <div className="min-h-screen flex items-end md:items-center justify-center p-0 md:p-4">
            <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-3xl shadow-2xl my-0 md:my-8 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-orange-500 p-5 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    🗑️ Delete Customer
                  </h3>
                  <button onClick={() => setDeleteCustomer(null)} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center text-2xl">×</button>
                </div>
              </div>

              {/* Body */}
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {(deleteCustomer.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{deleteCustomer.name}</div>
                    <div className="text-xs text-gray-500">
                      {deleteCustomer.totalInvoices || 0} invoices · {formatCurrency(deleteCustomer.totalSpent)}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  How would you like to remove this customer?
                </p>

                <div className="space-y-3">
                  {/* Hard Delete - Permanent */}
                  <button
                    onClick={() => handleDelete(deleteCustomer, 'hard')}
                    className="w-full p-4 rounded-xl bg-red-50 hover:bg-red-100 border-2 border-red-200 text-left transition"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🗑️</span>
                      <div className="flex-1">
                        <div className="font-bold text-red-700">Delete Permanently</div>
                        <div className="text-xs text-red-600 mt-0.5">
                          Customer is completely removed from database. This action cannot be undone.
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Soft Delete - Deactivate */}
                  {deleteCustomer.isActive && (
                    <button
                      onClick={() => handleDelete(deleteCustomer, 'soft')}
                      className="w-full p-4 rounded-xl bg-yellow-50 hover:bg-yellow-100 border-2 border-yellow-200 text-left transition"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">📦</span>
                        <div className="flex-1">
                          <div className="font-bold text-yellow-700">Deactivate (Archive)</div>
                          <div className="text-xs text-yellow-600 mt-0.5">
                            Hide from main list but keep history. Can be reactivated later.
                          </div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Cancel */}
                  <button
                    onClick={() => setDeleteCustomer(null)}
                    className="w-full p-3 rounded-xl bg-gray-100 hover:bg-gray-200 font-semibold text-gray-700 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} rounded-2xl p-3 md:p-4 text-white shadow-sm`}>
    <div className="text-2xl mb-1">{icon}</div>
    <div className="text-xs opacity-90 uppercase tracking-wide">{label}</div>
    <div className="text-lg md:text-xl font-bold mt-1">{value}</div>
  </div>
);

// Customer Card Component
const CustomerCard = ({ customer, onEdit, onDelete, onReactivate, formatCurrency, formatDate }) => (
  <div className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden ${!customer.isActive ? 'opacity-60' : ''}`}>
    {/* Header */}
    <div className={`p-4 text-white ${customer.isActive ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gradient-to-r from-gray-400 to-gray-500'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl flex-shrink-0">
            {(customer.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold truncate flex items-center gap-2">
              {customer.name}
              {!customer.isActive && (
                <span className="text-[10px] bg-white/30 px-2 py-0.5 rounded-full">INACTIVE</span>
              )}
            </h3>
            <p className="text-xs opacity-90">
              {customer.totalInvoices || 0} invoices · {customer.totalEstimates || 0} estimates
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs opacity-90">Total</div>
          <div className="font-bold">{formatCurrency(customer.totalSpent)}</div>
        </div>
      </div>
    </div>

    {/* Body */}
    <div className="p-4 space-y-2">
      {customer.email && (
        <a
          href={`mailto:${customer.email}`}
          className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
        >
          <span>✉️</span>
          <span className="truncate">{customer.email}</span>
        </a>
      )}
      {customer.phone && (
        <a
          href={`tel:${customer.phone}`}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600"
        >
          <span>📞</span>
          <span>{customer.phone}</span>
        </a>
      )}
      {customer.address && (
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <span>📍</span>
          <span className="line-clamp-2">{customer.address}</span>
        </div>
      )}
      {customer.acModel && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>🔧</span>
          <span>Model: {customer.acModel}</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-gray-400 pt-1">
        <span>🕒</span>
        <span>Last: {formatDate(customer.lastInvoiceDate)}</span>
      </div>
    </div>

    {/* Footer */}
    <div className="px-4 pb-4 flex gap-2">
      {customer.isActive ? (
        <>
          {customer.phone && (
            <a
              href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-xl text-xs font-medium text-center hover:bg-green-100"
            >
              💬 WhatsApp
            </a>
          )}
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-xl text-xs font-medium hover:bg-blue-100"
          >
            ✏️ Edit
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-2 bg-red-50 text-red-700 rounded-xl text-xs font-medium hover:bg-red-100"
          >
            🗑️
          </button>
        </>
      ) : (
        <>
          <button
            onClick={onReactivate}
            className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-xl text-xs font-medium hover:bg-green-100"
          >
            ♻️ Reactivate
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-2 bg-red-50 text-red-700 rounded-xl text-xs font-medium hover:bg-red-100"
          >
            🗑️ Delete Forever
          </button>
        </>
      )}
    </div>
  </div>
);

// Customer Form Modal
const CustomerFormModal = ({ customer, onClose, onSave, title }) => {
  const [formData, setFormData] = useState({
    name: customer.name || '',
    email: customer.email || '',
    phone: customer.phone || '',
    address: customer.address || '',
    city: customer.city || '',
    state: customer.state || '',
    zipCode: customer.zipCode || '',
    acModel: customer.acModel || '',
    notes: customer.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    onSave({ ...customer, ...formData });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="min-h-screen flex items-end md:items-center justify-center p-0 md:p-4">
        <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl shadow-2xl my-0 md:my-8 max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5 text-white sticky top-0 z-10 md:rounded-t-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">{title}</h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center text-2xl">
                ×
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData(p => ({ ...p, state: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData(p => ({ ...p, zipCode: e.target.value }))}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">AC Model</label>
              <input
                type="text"
                value={formData.acModel}
                onChange={(e) => setFormData(p => ({ ...p, acModel: e.target.value }))}
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                rows="3"
                className="w-full px-3 py-3 border border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
